import DOMPurify from 'dompurify';

// Sanitizer for rich-text post bodies (Suspire Circle).
//
// Distinct from utils/markdown.js: that one renders assistant chat replies and
// deliberately forbids raw HTML, images and styling. Circle bodies come from a
// WYSIWYG editor and from pasted content, so they legitimately contain block
// structure, inline marks and text alignment.
//
// The allowlist mirrors the server-side one in
// project/apps/core/utils/html_sanitizer.py — keep the two in sync. The server
// sanitizes on write; this sanitizes on render, so a row written before the
// server-side rule existed still can't inject anything.

export const RICH_TEXT_ALLOWED_TAGS = [
  // Block
  'p', 'div', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'pre',
  // Inline
  'span', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'strike',
  'sub', 'sup', 'mark', 'code',
  // Lists
  'ul', 'ol', 'li',
  // Links
  'a',
  // Tables — pasted content frequently contains them
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
];

export const RICH_TEXT_ALLOWED_ATTR = [
  'href', 'title', 'target', 'rel',
  'colspan', 'rowspan', 'scope',
  'style',
];

// Only these CSS properties survive in a style attribute. Everything else —
// positioning, backgrounds, font sizes, the mso-* soup Word pastes in — is
// dropped, which is what makes allowing `style` at all safe.
const ALLOWED_STYLE_PROPERTIES = new Set([
  'text-align',
  'font-weight',
  'font-style',
  'text-decoration',
]);

function filterStyleAttribute(node) {
  if (!node.getAttribute || !node.hasAttribute?.('style')) return;

  const kept = [];
  for (const declaration of node.getAttribute('style').split(';')) {
    const idx = declaration.indexOf(':');
    if (idx === -1) continue;
    const prop = declaration.slice(0, idx).trim().toLowerCase();
    const value = declaration.slice(idx + 1).trim();
    // Reject any value carrying a url()/expression() payload outright.
    if (!value || /url\s*\(|expression\s*\(/i.test(value)) continue;
    if (ALLOWED_STYLE_PROPERTIES.has(prop)) kept.push(`${prop}: ${value}`);
  }

  if (kept.length) node.setAttribute('style', kept.join('; '));
  else node.removeAttribute('style');
}

let hooksInstalled = false;

function installHooks() {
  if (hooksInstalled) return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    filterStyleAttribute(node);
    // Any link that opens in a new tab must not hand over window.opener.
    if (node.tagName === 'A' && node.getAttribute('target')) {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  hooksInstalled = true;
}

/** Sanitize rich-text HTML for rendering or storage. */
export function sanitizeRichText(html) {
  if (!html) return '';
  installHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR,
    // Block javascript:/data: URLs in href.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
}

/**
 * Clean HTML coming off the clipboard.
 *
 * Word and Google Docs paste enormous wrappers — <o:p>, <w:sdt>, class="MsoNormal",
 * per-character <span style="font-family:...;font-size:...">. Running the paste
 * through the same allowlist keeps the *structure* the user wanted (headings,
 * bold, lists, links) and discards the vendor noise, so the pasted text adopts
 * the editor's own typography instead of dragging its source styling along.
 */
export function sanitizePastedHtml(html) {
  const clean = sanitizeRichText(html);
  if (!clean) return '';

  const holder = document.createElement('div');
  holder.innerHTML = clean;

  // Unwrap spans that carry nothing after style filtering — Word emits one per
  // run of characters and they'd otherwise pile up in the document.
  holder.querySelectorAll('span').forEach((span) => {
    if (span.attributes.length === 0) span.replaceWith(...span.childNodes);
  });

  // Drop empty block elements left behind by the strip.
  holder.querySelectorAll('p, div, span, h1, h2, h3, h4, h5, h6').forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector('br, img, hr, table')) el.remove();
  });

  return holder.innerHTML;
}

/** True when a body should be rendered as HTML rather than plain text. */
export function isHtmlBody(post) {
  return post?.body_format === 'HTML';
}

// Block-level boundaries that should read as a space once tags are gone.
// Only block tags — turning inline tags into spaces would split "<b>Hi</b>!"
// into "Hi !".
const BLOCK_BOUNDARY = /<\/(p|div|h[1-6]|li|tr|blockquote|pre|td|th|caption|table|ul|ol)>|<br\s*\/?>|<hr\s*\/?>/gi;

/** Strip tags for previews, meta descriptions and character counts. */
export function richTextToPlain(html) {
  if (!html) return '';
  const holder = document.createElement('div');
  // Insert the separator before parsing, otherwise textContent runs adjacent
  // blocks together ("<p>Hi</p><p>there</p>" -> "Hithere").
  holder.innerHTML = sanitizeRichText(html).replace(BLOCK_BOUNDARY, '$& ');
  return (holder.textContent || '').replace(/\s+/g, ' ').trim();
}
